import { BEMBlock } from 'helpers/classnames';
import React, {
    useState, useEffect, useCallback, useMemo,
} from 'react';
import { Image } from 'palette/Image';
import { useProductCardContext } from 'palette/ProductCard';
import ImagesCarousel from 'palette/ProductCard/ProductCardBlocks/ImagesCarousel';
import { OnCardLinkClick } from 'palette/ProductCard/types/cardElements';
import { IImageObject } from 'palette/ProductCard/types/productData';
import '../styles/ProductCardMainSection.scss';

type ProductCardMainSectionProps = React.PropsWithChildren<{
    image?: IImageObject;
    alternativeImage: IImageObject | null;
    productUrl: string;
    onClick: OnCardLinkClick;
    onLinkContextMenu: OnCardLinkClick;
    altText: string;
    allowVideo: boolean;
    modelImageMode: boolean;
    carouselImages?: IImageObject[];
    scrollIndicatorAnimation?: boolean;
    video?: { url: string };
}>;

function ProductCardMainSection({
    video,
    image,
    alternativeImage,
    productUrl,
    children,
    onClick,
    onLinkContextMenu,
    altText,
    allowVideo,
    modelImageMode,
    carouselImages = [],
    scrollIndicatorAnimation = false,
}: ProductCardMainSectionProps) {
    const {
        orientation: cardOrientation,
        size: cardSize,
        imageBackground,
        isDisabled,
        isHovered,
        isThumbnailsHovered,
        isLazy,
    } = useProductCardContext();
    const [isFullCardImage, setIsFullCardImage] = useState(false);
    const [block, BEMElement] = BEMBlock('palette-product-card-main-section', {
        [`${imageBackground}-background`]: imageBackground !== undefined,
        [cardOrientation]: cardOrientation !== undefined,
    });
    const showImagesCarousel = carouselImages?.length > 1;

    const getAttributes = () => {
        if (modelImageMode && alternativeImage?.attributes) {
            return {
                mainAttributes: alternativeImage?.attributes,
                alternativeAttributes: image?.attributes,
                isMainFullCardImage: alternativeImage?.isFullCardImage,
                isAlternativeFullCardImage: image?.isFullCardImage,
            };
        }

        return {
            mainAttributes: image?.attributes,
            alternativeAttributes: alternativeImage?.attributes,
            isMainFullCardImage: image?.isFullCardImage,
            isAlternativeFullCardImage: alternativeImage?.isFullCardImage,
        };
    };

    const {
        mainAttributes,
        alternativeAttributes,
        isMainFullCardImage,
        isAlternativeFullCardImage,
    } = getAttributes();

    const showVideo = allowVideo && video && !isHovered;
    const showAlternativeImage = !video && isHovered && !isThumbnailsHovered && alternativeAttributes;
    const imageAttributes = showAlternativeImage ? alternativeAttributes : mainAttributes;
    const isFullCard = useMemo(() => {
        if (showImagesCarousel) {
            return false;
        }

        return showAlternativeImage ? isAlternativeFullCardImage : isMainFullCardImage;
    }, [showImagesCarousel, showAlternativeImage, isAlternativeFullCardImage, isMainFullCardImage]);

    const handleImageLoad = useCallback(() => {
        if (image !== undefined) {
            setIsFullCardImage(isFullCard!);
        }
    }, [image, isFullCard]);

    const getContent = () => {
        if (showVideo) {
            return (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={BEMElement('video', { 'full-card': isFullCard })}
                    src={video.url}
                    width={100}
                />
            );
        }

        if (showImagesCarousel) {
            return (
                <ImagesCarousel
                    images={carouselImages}
                    scrollIndicatorAnimation={showImagesCarousel && scrollIndicatorAnimation}
                />
            );
        }

        return (
            <Image
                className={BEMElement('image', { 'full-card': isFullCardImage })}
                onLoad={handleImageLoad}
                {...isLazy && { loading: 'lazy' }}
                {...imageAttributes}
                alt={altText}
            />
        );
    };

    useEffect(() => {
        handleImageLoad();
    }, [handleImageLoad]);

    return (
        <div className={block}>
            <a
                className={BEMElement('link', {
                    [`${cardSize}-card`]: cardSize !== undefined,
                })}
                href={productUrl}
                onClick={onClick}
                onContextMenu={onLinkContextMenu}
            >
                {isDisabled && (
                    <div className={BEMElement('disabled-cover', { visible: isDisabled })} />
                )}
                {getContent()}
            </a>
            {children}
        </div>
    );
}

export default ProductCardMainSection;
